import apiService from './api';
import { unwrap } from '../utils/apiResponse';
import { downloadBlob } from '../utils/downloadBlob';
import type {
  AuditEngagement,
  AuditFinding,
  AutoChecks,
  Checklist,
  CheckpointStatus,
  ComplianceAccess,
  CompliancePack,
  ComplianceReport,
  EngagementDetail,
  EngagementInput,
  PackInput,
  SectionKey,
} from '../types/compliance';

// ============================================================================
// COMPLIANCE SERVICE — /api/compliance
// Season packs, auditor access and self-assessment. Each report section needs
// the seat that already manages those records (spray admin, timesheet HR,
// farm_jobs admin/supervisor); the server narrows everything to those.
// ============================================================================

const safeName = (s: string) => s.replace(/[^\w.-]+/g, '-').slice(0, 80) || 'compliance-pack';

class ComplianceService {
  async getAccess(): Promise<ComplianceAccess> {
    return unwrap(await apiService.get<ComplianceAccess>('/compliance/access'));
  }

  async getReport(opts: {
    start: string; end: string; sections: SectionKey[]; farmIds?: number[] | null; pseudonymise?: boolean;
  }): Promise<{ report: ComplianceReport; checks: AutoChecks }> {
    const p = new URLSearchParams({ start: opts.start, end: opts.end, sections: opts.sections.join(',') });
    if (opts.farmIds && opts.farmIds.length) p.set('farm_ids', opts.farmIds.join(','));
    if (opts.pseudonymise) p.set('pseudonymise', 'true');
    // A season of records takes more than the default 10s to gather on a big farm.
    return unwrap(await apiService.get<{ report: ComplianceReport; checks: AutoChecks }>(
      `/compliance/report?${p}`, { timeout: 60000 },
    ));
  }

  async listPacks(): Promise<CompliancePack[]> {
    return unwrap(await apiService.get<CompliancePack[]>('/compliance/packs')) ?? [];
  }

  async createPack(input: PackInput): Promise<CompliancePack> {
    return unwrap(await apiService.post<CompliancePack>('/compliance/packs', input, undefined, { timeout: 60000 }));
  }

  async downloadPackPdf(pack: CompliancePack): Promise<void> {
    const blob = await (apiService.get(`/compliance/packs/${pack.id}/pdf`, { responseType: 'blob', timeout: 60000 }) as unknown as Promise<Blob>);
    downloadBlob(blob, `${safeName(pack.title)}.pdf`);
  }

  async downloadPackCsv(pack: CompliancePack): Promise<void> {
    const blob = await (apiService.get(`/compliance/packs/${pack.id}/spray-register.csv`, { responseType: 'blob' }) as unknown as Promise<Blob>);
    downloadBlob(blob, `${safeName(pack.title)}-spray-register.csv`);
  }

  async getChecklist(standardKey: string, period?: { start: string; end: string }): Promise<Checklist> {
    const q = period ? `?start=${period.start}&end=${period.end}` : '';
    return unwrap(await apiService.get<Checklist>(`/compliance/standards/${standardKey}${q}`, { timeout: 60000 }));
  }

  async saveCheckpoint(standardKey: string, checkpointKey: string, data: {
    status: CheckpointStatus; clause_ref: string | null; note: string | null; evidence_url: string | null;
  }): Promise<void> {
    unwrap(await apiService.put(`/compliance/standards/${standardKey}/checkpoints/${checkpointKey}`, data));
  }

  async recordSelfAssessment(standardKey: string): Promise<void> {
    unwrap(await apiService.post(`/compliance/standards/${standardKey}/self-assessments`, {}, undefined, { timeout: 60000 }));
  }

  async listEngagements(): Promise<AuditEngagement[]> {
    return unwrap(await apiService.get<AuditEngagement[]>('/compliance/engagements')) ?? [];
  }

  async createEngagement(input: EngagementInput): Promise<{ engagement: AuditEngagement; link: string; email_sent: boolean }> {
    return unwrap(await apiService.post<{ engagement: AuditEngagement; link: string; email_sent: boolean }>(
      '/compliance/engagements', input,
    ));
  }

  async getEngagement(id: number): Promise<EngagementDetail> {
    return unwrap(await apiService.get<EngagementDetail>(`/compliance/engagements/${id}`));
  }

  async revokeEngagement(id: number): Promise<AuditEngagement> {
    return unwrap(await apiService.post<AuditEngagement>(`/compliance/engagements/${id}/revoke`, {}));
  }

  async resendEngagementLink(id: number): Promise<{ link: string; email_sent: boolean }> {
    return unwrap(await apiService.post<{ link: string; email_sent: boolean }>(`/compliance/engagements/${id}/resend`, {}));
  }

  async respondToFinding(engagementId: number, findingId: number, data: {
    org_response?: string | null; status?: 'open' | 'closed' | null; raise_action?: boolean; due_date?: string | null;
  }): Promise<AuditFinding> {
    return unwrap(await apiService.patch<AuditFinding>(`/compliance/engagements/${engagementId}/findings/${findingId}`, data));
  }
}

export default new ComplianceService();
